import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import { Minus, Plus, RotateCcw } from 'lucide-react';
import { CARD_IMAGE_ASPECT, DEFAULT_CROP, cropRect, type ImageCrop } from '../../../../packages/shared/src/image-crop.js';

export type FramerFragrance = { name: string; brand?: { name: string } | null; concentration?: string; isOriginal?: boolean };
type Size = { width: number; height: number };
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const clampCrop = (crop: ImageCrop): ImageCrop => ({ zoom: clamp(crop.zoom, 0.5, 5), cx: clamp(crop.cx, -0.5, 1.5), cy: clamp(crop.cy, -0.5, 1.5) });

/** Positions the original inside a card-shaped frame exactly as the server will render it. */
function Framed({ src, size, crop, className, alt = '' }: { src: string; size: Size; crop: ImageCrop; className?: string; alt?: string }) {
  const rect = cropRect(size.width, size.height, crop);
  const style: CSSProperties = {
    width: (size.width / rect.width) * 100 + '%',
    height: (size.height / rect.height) * 100 + '%',
    left: (-rect.left / rect.width) * 100 + '%',
    top: (-rect.top / rect.height) * 100 + '%',
  };
  return <div className={'framed-image ' + (className ?? '')} style={{ aspectRatio: String(CARD_IMAGE_ASPECT) }}>
    <img src={src} alt={alt} style={style} draggable={false} />
  </div>;
}

/**
 * Pan and zoom a picture inside the catalogue card's frame, with a live card preview beside it.
 * Nothing is baked in: the framing is saved alongside the untouched original and can be changed later.
 */
export function ImageFramer({ src, crop, onChange, fragrance, disabled = false }: {
  src: string; crop: ImageCrop | null; onChange: (crop: ImageCrop | null) => void; fragrance: FramerFragrance; disabled?: boolean;
}) {
  const viewport = useRef<HTMLDivElement>(null), drag = useRef<{ x: number; y: number } | null>(null);
  const [size, setSize] = useState<Size | null>(null), [failed, setFailed] = useState(false);
  const current = crop ?? DEFAULT_CROP;
  const latest = useRef(current);
  latest.current = current;
  useEffect(() => { setSize(null); setFailed(false); }, [src]);
  const update = (next: ImageCrop) => onChange(clampCrop(next));
  // React's wheel listener is passive, so zooming without scrolling the dialog needs a native one.
  useEffect(() => {
    const element = viewport.current;
    if (!element || disabled) return;
    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      update({ ...latest.current, zoom: latest.current.zoom * Math.exp(-event.deltaY * 0.0015) });
    };
    element.addEventListener('wheel', wheel, { passive: false });
    return () => element.removeEventListener('wheel', wheel);
  });
  function pan(dx: number, dy: number) {
    const element = viewport.current;
    if (!element || !size) return;
    const rect = cropRect(size.width, size.height, latest.current);
    update({ ...latest.current, cx: latest.current.cx - (dx / element.clientWidth) * (rect.width / size.width), cy: latest.current.cy - (dy / element.clientHeight) * (rect.height / size.height) });
  }
  function down(event: PointerEvent) {
    if (disabled) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { x: event.clientX, y: event.clientY };
  }
  function move(event: PointerEvent) {
    if (!drag.current) return;
    pan(event.clientX - drag.current.x, event.clientY - drag.current.y);
    drag.current = { x: event.clientX, y: event.clientY };
  }
  function key(event: KeyboardEvent) {
    const step = event.shiftKey ? 20 : 6;
    const moves: Record<string, [number, number]> = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
    if (moves[event.key]) { event.preventDefault(); pan(...moves[event.key]!); }
    else if (event.key === '+' || event.key === '=') { event.preventDefault(); update({ ...current, zoom: current.zoom * 1.1 }); }
    else if (event.key === '-') { event.preventDefault(); update({ ...current, zoom: current.zoom / 1.1 }); }
  }
  if (failed) return <p className="error-message" role="alert">This picture could not be loaded for framing.</p>;
  return <div className="image-framer">
    <img className="framer-probe" src={src} alt="" onLoad={event => setSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })} onError={() => setFailed(true)} />
    <div className="framer-editor">
      <div ref={viewport} className={'framer-viewport' + (disabled ? ' is-disabled' : '')} tabIndex={disabled ? -1 : 0} role="application"
        aria-label="Image framing. Drag or use the arrow keys to move the picture; scroll or press plus and minus to zoom."
        onPointerDown={down} onPointerMove={move} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }} onKeyDown={key}>
        {size ? <Framed src={src} size={size} crop={current} /> : <span className="framer-loading">Loading picture…</span>}
      </div>
      <div className="framer-controls">
        <button type="button" className="framer-icon" disabled={disabled} onClick={() => update({ ...current, zoom: current.zoom / 1.15 })} aria-label="Zoom out"><Minus size={15} aria-hidden /></button>
        <label className="framer-zoom"><span className="sr-only">Zoom</span>
          <input type="range" min={0.5} max={5} step={0.01} value={current.zoom} disabled={disabled} onChange={event => update({ ...current, zoom: Number(event.target.value) })} />
        </label>
        <button type="button" className="framer-icon" disabled={disabled} onClick={() => update({ ...current, zoom: current.zoom * 1.15 })} aria-label="Zoom in"><Plus size={15} aria-hidden /></button>
        <span className="framer-zoom-value" aria-live="polite">{Math.round(current.zoom * 100)}%</span>
      </div>
      <button type="button" className="text-link framer-reset" disabled={disabled || !crop} onClick={() => onChange(null)}><RotateCcw size={13} aria-hidden /> Show the whole picture</button>
    </div>
    <figure className="framer-preview">
      <figcaption>Card preview</figcaption>
      <div className="product-card" aria-hidden="true">
        <div className="product-photo">
          {size ? crop ? <Framed src={src} size={size} crop={crop} className="bottle-image" /> : <img className="bottle-image" src={src} alt="" /> : null}
        </div>
        <div className="product-copy">
          <span className="product-brand">{fragrance.brand?.name}</span>
          <h2>{fragrance.name}</h2>
          <p>{fragrance.concentration || 'Concentration under review'}</p>
        </div>
      </div>
    </figure>
  </div>;
}
